/**
 * The site's pages, and the one place a URL is spelled.
 *
 * This page was a single prerendered document per locale until it had to carry
 * a product, a security story, a company and a way to reach it. Five documents
 * per locale rather than a router: the whole reason the build prerenders is that
 * WhatsApp, LinkedIn, Bing and Yandex read bytes and never run a script, and a
 * client-side router would give every page after the first the empty `<title>`
 * this file exists to prevent.
 *
 * **The slug is translated.** `/tr/urun/` and `/en/product/` are the same page
 * in two languages, and a Turkish-first site whose URLs are English is a site
 * that reads as a translation of something else. It costs one lookup here and
 * buys the language switch its only hard requirement: a reader who switches
 * language from the security page must land on the security page, not the home
 * page — which is what a naive `/tr/` ↔ `/en/` swap does to every URL with a
 * path on it.
 */

import type { Locale } from './i18n'

/** Every page, in navigation order. `home` is the locale root. */
export type PageId = 'home' | 'product' | 'security' | 'company' | 'contact'

export const PAGES: readonly PageId[] = ['home', 'product', 'security', 'company', 'contact']

/** The pages the header lists, which is every page except the one the brand
 *  already links to. */
export const NAV_PAGES: readonly PageId[] = ['product', 'security', 'company', 'contact']

/**
 * The URL segment for each page in each language.
 *
 * Turkish is written without its diacritics on purpose: `urun` rather than
 * `ürün`. A percent-encoded path is what gets pasted into a chat window as
 * `%C3%BCr%C3%BCn`, and the one place this site cannot afford to look broken is
 * the link somebody forwards.
 */
const SLUGS: Record<PageId, Record<Locale, string>> = {
  home: { tr: '', en: '' },
  product: { tr: 'urun', en: 'product' },
  security: { tr: 'guvenlik', en: 'security' },
  company: { tr: 'kurumsal', en: 'company' },
  contact: { tr: 'iletisim', en: 'contact' },
}

/** The path a page is served at, always with a trailing slash.
 *
 *  Trailing because each page is a directory holding an `index.html`, and
 *  because a link that lands on nginx's own redirect costs the reader a round
 *  trip before the first byte of the document they asked for. */
export function pathFor(locale: Locale, page: PageId): string {
  const slug = SLUGS[page][locale]
  return slug ? `/${locale}/${slug}/` : `/${locale}/`
}

/** Which page a path names, or null when it names none.
 *
 *  Used by the browser entry only as a fallback: the page, like the locale, is
 *  read from what the prerender stamped on `<html>`, because one source at
 *  runtime cannot disagree with itself. */
export function pageFromPath(pathname: string, locale: Locale): PageId | null {
  const prefix = `/${locale}/`
  if (!pathname.startsWith(prefix)) return null
  const rest = pathname.slice(prefix.length).replace(/\/+$/, '')
  if (rest === '') return 'home'
  return PAGES.find((page) => SLUGS[page][locale] === rest) ?? null
}

/** Narrows a runtime string to a known page, for the `data-page` attribute. */
export function isPage(value: string | null | undefined): value is PageId {
  return PAGES.includes(value as PageId)
}
